/**
 * Company Report page: the full institutional research view for one
 * company — research scores, fundamentals/ratios, qualitative profile,
 * and the explainable strengths/weaknesses/open-questions narrative.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { apiClient } from "@/api/client";
import type { AxiosError } from "axios";
import type { Company, CompanyRiskAssessment, CompanyProfile, FinancialStatement, FundamentalSnapshot, PriceBar, ResearchReport } from "@/types";
import CandlestickChart from "@/components/charts/CandlestickChart";
import { showNotification } from "@/desktop/tauriBridge";
import { usePerformanceMode } from "@/hooks/usePerformanceMode";
import ScoreBadge from "@/components/research/ScoreBadge";
import CommitteeSection from "@/components/research/CommitteeSection";
import AgentReasoningPanel from "@/components/research/AgentReasoningPanel";
import ForecastPanel from "@/components/research/ForecastPanel";
import CompanyTimeline from "@/components/research/CompanyTimeline";
import TechnicalAnalysisPanel from "@/components/research/TechnicalAnalysisPanel";
import FundamentalAnalysisPanel from "@/components/research/FundamentalAnalysisPanel";
import FilingsPanel from "@/components/research/FilingsPanel";
import PriceOutlookPanel from "@/components/research/PriceOutlookPanel";
import StockOverviewPanel from "@/components/research/StockOverviewPanel";
import HistoricalPEChart from "@/components/research/HistoricalPEChart";
import ReportExportButtons from "@/components/research/ReportExportButtons";

type ScoreKey =
  | "valuation_score"
  | "growth_score"
  | "quality_score"
  | "financial_health_score"
  | "profitability_score"
  | "risk_score"
  | "momentum_score"
  | "news_score"
  | "macro_score";

type NullableScoreKey =
  | "leadership_score"
  | "competitive_position_score"
  | "innovation_score"
  | "technical_score";

const SCORE_ROWS: Array<{ key: ScoreKey; label: string }> = [
  { key: "valuation_score", label: "Valuation" },
  { key: "growth_score", label: "Growth" },
  { key: "quality_score", label: "Quality" },
  { key: "financial_health_score", label: "Financial health" },
  { key: "profitability_score", label: "Profitability" },
  { key: "risk_score", label: "Risk profile" },
  { key: "momentum_score", label: "Momentum" },
  { key: "news_score", label: "News sentiment" },
  { key: "macro_score", label: "Macro backdrop" },
];

// Added in methodology 1.2 — nullable because reports generated under
// earlier methodology versions don't have these. Rendered separately so
// the required rows above never need a null check.
const NULLABLE_SCORE_ROWS: Array<{ key: NullableScoreKey; label: string }> = [
  { key: "leadership_score", label: "Leadership" },
  { key: "competitive_position_score", label: "Competitive position" },
  { key: "innovation_score", label: "Innovation" },
  { key: "technical_score", label: "Technical strength" },
];

function fmtRatio(
  value: string | number | null | undefined,
  suffix = "",
  decimals = 2,
): string {
  // Decimal-backed fields are serialized as strings over JSON, so always
  // coerce with Number() before formatting rather than trusting the static
  // type — calling .toFixed() on a string throws and blanks the page.
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  return Number.isFinite(num) ? `${num.toFixed(decimals)}${suffix}` : "—";
}

export default function CompanyReport(): React.ReactElement {
  const { companyId } = useParams<{ companyId: string }>();
  const performanceProfile = usePerformanceMode();
  const [company, setCompany] = useState<Company | null>(null);
  const [report, setReport] = useState<ResearchReport | null>(null);
  const [fundamentals, setFundamentals] = useState<FundamentalSnapshot | null>(null);
  const [financialStatements, setFinancialStatements] = useState<FinancialStatement[]>([]);
  const [priceBars, setPriceBars] = useState<PriceBar[]>([]);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [risk, setRisk] = useState<CompanyRiskAssessment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState<{ done: number; total: number }>({ done: 0, total: 7 });
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isAutoGenerating, setIsAutoGenerating] = useState(false);
  const autoGenerateAttempted = useRef<string | null>(null);
  const [fetchMsg, setFetchMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    setError(null);
    setLoadProgress({ done: 0, total: 7 });

    // Each request counts toward progress as soon as it SETTLES, whether it
    // succeeds or fails — a stock with no report yet still finishes loading,
    // it just has less data to show. Requests run in parallel for speed; the
    // bar fills in whatever order they actually complete.
    const track = <T,>(promise: Promise<T>): Promise<T> =>
      promise.finally(() => setLoadProgress((prev) => ({ ...prev, done: prev.done + 1 })));

    try {
      const [companyRes, riskRes] = await Promise.all([
        track(apiClient.get<Company>(`/companies/${companyId}`)),
        track(apiClient.get<CompanyRiskAssessment>(`/companies/${companyId}/risk`).catch(() => null)),
      ]);
      setCompany(companyRes.data);
      setRisk(riskRes?.data ?? null);

      await Promise.all([
        track(
          apiClient
            .get<ResearchReport>(`/companies/${companyId}/research/latest`)
            .then((r) => setReport(r.data))
            .catch(() => setReport(null)),
        ),
        track(
          apiClient
            .get<FundamentalSnapshot>(`/companies/${companyId}/fundamentals/latest`)
            .then((r) => setFundamentals(r.data))
            .catch(() => setFundamentals(null)),
        ),
        track(
          apiClient
            .get<FinancialStatement[]>(`/companies/${companyId}/financial-statements`)
            .then((r) => setFinancialStatements(r.data))
            .catch(() => setFinancialStatements([])),
        ),
        track(
          apiClient
            .get<PriceBar[]>(`/companies/${companyId}/price-bars`, { params: { lookback_days: 365 } })
            .then((r) => setPriceBars(r.data))
            .catch(() => setPriceBars([])),
        ),
        track(
          apiClient
            .get<CompanyProfile>(`/companies/${companyId}/profile`)
            .then((r) => setProfile(r.data))
            .catch(() => setProfile(null)),
        ),
      ]);
    } catch {
      setError("Unable to load this company.");
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Auto-generate a first report so opening a stock always shows one, rather
  // than requiring a manual click. Runs once per company (tracked via a ref,
  // not state, so it can't re-trigger on every render) and only after the
  // initial data load has actually finished and confirmed no report exists.
  useEffect(() => {
    if (isLoading || !companyId || report) return;
    if (autoGenerateAttempted.current === companyId) return;
    autoGenerateAttempted.current = companyId;

    let cancelled = false;
    (async () => {
      setIsAutoGenerating(true);
      try {
        const { data } = await apiClient.post<ResearchReport>(`/companies/${companyId}/research`);
        if (!cancelled) setReport(data);
      } catch {
        // Leave the manual "Generate report" button as the fallback; no need
        // to surface an error for a background attempt the user didn't ask for.
      } finally {
        if (!cancelled) setIsAutoGenerating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoading, companyId, report]);

  const handleFetchData = async (): Promise<void> => {
    if (!company) return;
    setIsFetching(true);
    setFetchMsg(null);
    try {
      const { data } = await apiClient.post<{ prices?: { available?: boolean; note?: string; records?: number } }>(
        `/providers/sync/${company.symbol}`,
      );
      const prices = data.prices;
      if (prices && prices.available !== false) {
        setFetchMsg(
          `Loaded price data${prices.records ? ` (${prices.records} new bars)` : ""}. Refreshing charts…`,
        );
      } else {
        setFetchMsg(prices?.note ?? "Fundamentals loaded, but prices are unavailable from the free sources.");
      }
      await loadAll(); // re-pull so the price chart + P/E history render
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setFetchMsg(detail ? String(detail) : "Couldn't fetch data for this stock.");
    } finally {
      setIsFetching(false);
    }
  };

  const handleGenerateReport = async (): Promise<void> => {
    if (!companyId) return;
    setIsGenerating(true);
    setError(null);
    try {
      const { data } = await apiClient.post<ResearchReport>(`/companies/${companyId}/research`);
      setReport(data);
      void showNotification(
        "Research report ready",
        `${company?.symbol ?? "Company"} scored ${Number(data.overall_score).toFixed(0)}/100 overall.`
      );
    } catch (err) {
      const axiosError = err as AxiosError<{ detail: string }>;
      setError(axiosError.response?.data?.detail ?? "Unable to generate a research report.");
    } finally {
      setIsGenerating(false);
    }
  };

  if (isLoading) {
    const pct = Math.round((loadProgress.done / Math.max(loadProgress.total, 1)) * 100);
    return (
      <div className="panel mx-auto max-w-md p-6">
        <div className="mb-1 flex justify-between text-xs text-mist-400">
          <span>Loading company report… ({loadProgress.done}/{loadProgress.total})</span>
          <span>{pct}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-ink-800">
          <div
            className="h-full rounded-full bg-gradient-to-r from-signal-teal to-signal-violet transition-all duration-300"
            style={{
              width: `${pct}%`,
              boxShadow: "0 0 12px -2px rgba(47, 211, 166, 0.5)",
            }}
          />
        </div>
      </div>
    );
  }

  if (!company) {
    return <p className="text-sm text-signal-rose">{error ?? "Company not found."}</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-sm text-signal-teal">{company.symbol}</p>
          <h1 className="text-2xl font-semibold">{company.name}</h1>
          <p className="mt-1 text-sm text-mist-400">
            {company.sector ?? "Unclassified sector"}
            {company.industry ? ` · ${company.industry}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleFetchData}
            disabled={isFetching}
            className="rounded-lg border border-signal-teal/40 bg-signal-teal/10 px-4 py-2 text-sm font-medium text-signal-teal transition hover:bg-signal-teal/20 disabled:opacity-60"
          >
            {isFetching ? "Fetching…" : "Fetch latest data"}
          </button>
          {report && companyId && <ReportExportButtons companyId={companyId} />}
          <button
            onClick={handleGenerateReport}
            disabled={isGenerating || isAutoGenerating}
            className="btn-primary"
          >
            {isGenerating || isAutoGenerating
              ? "Generating…"
              : report
                ? "Regenerate report"
                : "Generate report"}
          </button>
        </div>
      </div>

      {fetchMsg && <p className="text-sm text-mist-300">{fetchMsg}</p>}
      {error && <p className="text-sm text-signal-rose">{error}</p>}

      <StockOverviewPanel
        companyId={company.id}
        symbol={company.symbol}
        priceBars={priceBars}
        fundamentals={fundamentals}
        risk={risk}
      />

      {!report ? (
        <div className="panel flex flex-col items-center gap-2 py-16 text-center">
          {isAutoGenerating ? (
            <>
              <div className="relative h-8 w-8">
                <div className="absolute inset-0 animate-spin rounded-full border-2 border-signal-teal border-t-transparent" />
                <div className="absolute inset-0 animate-pulse-ring rounded-full border border-signal-teal/40" />
              </div>
              <p className="mt-2 text-sm font-medium text-mist-200">
                Convening the research committee…
              </p>
              <p className="max-w-sm text-sm text-mist-400">
                Fundamentals, market and technical, company intelligence, news, macro, and risk
                are being scored now — this only takes a moment.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-mist-200">No research report yet</p>
              <p className="max-w-sm text-sm text-mist-400">
                Generate one to convene the full AI research committee — fundamentals, market
                and technical, company intelligence, news, macro, and risk — with an SEC-filing
                advisor, then read their executive summary, bull and bear cases, and documented
                disagreements.
              </p>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="panel p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-mist-400">Overall research score</p>
                <p className="mt-1 font-mono text-4xl font-semibold text-mist-100 animate-count-glow">
                  {Number(report.overall_score).toFixed(0)}
                  <span className="text-lg text-mist-400">/100</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-mist-400">Confidence</p>
                <p className="mt-1 font-mono text-2xl text-mist-200">
                  {Number(report.confidence_score).toFixed(0)}%
                </p>
              </div>
            </div>

            {/* Animated overall score bar */}
            <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-ink-800">
              <div
                className="h-full origin-left animate-bar-grow rounded-full bg-gradient-to-r from-signal-teal to-signal-violet"
                style={{
                  width: `${Math.max(0, Math.min(100, Number(report.overall_score)))}%`,
                  boxShadow: "0 0 10px -2px rgba(47, 211, 166, 0.4)",
                }}
              />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {SCORE_ROWS.map(({ key, label }) => (
                <div key={key} className="rounded-lg border border-ink-700 bg-ink-800/50 p-3">
                  <p className="text-xs text-mist-400">{label}</p>
                  <div className="mt-1">
                    <ScoreBadge score={report[key]} />
                  </div>
                </div>
              ))}
              {NULLABLE_SCORE_ROWS.filter(({ key }) => report[key] !== null).map(({ key, label }) => (
                <div key={key} className="rounded-lg border border-ink-700 bg-ink-800/50 p-3">
                  <p className="text-xs text-mist-400">{label}</p>
                  <div className="mt-1">
                    <ScoreBadge score={report[key] as number} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <AgentReasoningPanel report={report} />

          <CommitteeSection report={report} />

          {companyId && <TechnicalAnalysisPanel companyId={companyId} />}
          {companyId && <FundamentalAnalysisPanel companyId={companyId} />}
          {companyId && <FilingsPanel companyId={companyId} />}

          {companyId && (
            <PriceOutlookPanel
              companyId={companyId}
              currentPrice={
                priceBars.length > 0 ? Number(priceBars[priceBars.length - 1].close) : null
              }
            />
          )}

          {priceBars.length > 0 && fundamentals && (
            <HistoricalPEChart priceBars={priceBars} eps={fundamentals.eps} />
          )}

          {priceBars.length > 0 && (
            <div className="panel p-6">
              <h2 className="mb-4 text-lg font-semibold text-mist-100">Price history</h2>
              <CandlestickChart
                data={priceBars.map((bar) => ({
                  time: bar.price_date,
                  open: Number(bar.open),
                  high: Number(bar.high),
                  low: Number(bar.low),
                  close: Number(bar.close),
                }))}
                targetFps={performanceProfile?.chart_target_fps ?? 60}
              />
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="panel p-5">
              <h2 className="font-semibold text-signal-teal">Bull case</h2>
              <ul className="mt-3 space-y-2 text-sm text-mist-200">
                {(report.bull_case ?? report.strengths).length === 0 && (
                  <li className="text-mist-400">None identified.</li>
                )}
                {(report.bull_case ?? report.strengths).map((s, i) => (
                  <li key={i}>• {s}</li>
                ))}
              </ul>
            </div>
            <div className="panel p-5">
              <h2 className="font-semibold text-signal-rose">Bear case</h2>
              <ul className="mt-3 space-y-2 text-sm text-mist-200">
                {(report.bear_case ?? report.weaknesses).length === 0 && (
                  <li className="text-mist-400">None identified.</li>
                )}
                {(report.bear_case ?? report.weaknesses).map((s, i) => (
                  <li key={i}>• {s}</li>
                ))}
              </ul>
            </div>
            <div className="panel p-5">
              <h2 className="font-semibold text-signal-amber">Open questions</h2>
              <ul className="mt-3 space-y-2 text-sm text-mist-200">
                {report.open_questions.length === 0 && <li className="text-mist-400">None.</li>}
                {report.open_questions.map((s, i) => (
                  <li key={i}>• {s}</li>
                ))}
              </ul>
            </div>
          </div>

          {companyId && <ForecastPanel companyId={companyId} />}
          {companyId && <CompanyTimeline companyId={companyId} />}
        </>
      )}

      {financialStatements.length > 0 && (
        <div className="panel overflow-x-auto p-6">
          <h2 className="text-lg font-semibold text-mist-100">Financial statements</h2>
          <table className="mt-4 w-full text-left text-sm">
            <thead>
              <tr className="border-b border-ink-700 text-xs uppercase tracking-wide text-mist-400">
                <th className="py-2 pr-4">Period</th>
                <th className="py-2 pr-4 text-right">Revenue</th>
                <th className="py-2 pr-4 text-right">Net income</th>
                <th className="py-2 pr-4 text-right">Op. cash flow</th>
                <th className="py-2 pr-4 text-right">Free cash flow</th>
                <th className="py-2 text-right">EPS (diluted)</th>
              </tr>
            </thead>
            <tbody>
              {financialStatements.map((statement) => (
                <tr key={statement.id} className="border-b border-ink-800 last:border-0">
                  <td className="py-2 pr-4 text-mist-200">
                    {statement.period_type === "annual"
                      ? `FY${statement.fiscal_year}`
                      : `FY${statement.fiscal_year} Q${statement.fiscal_quarter}`}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-mist-200">
                    {statement.revenue ? `$${Number(statement.revenue).toLocaleString()}` : "—"}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-mist-200">
                    {statement.net_income ? `$${Number(statement.net_income).toLocaleString()}` : "—"}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-mist-200">
                    {statement.operating_cash_flow
                      ? `$${Number(statement.operating_cash_flow).toLocaleString()}`
                      : "—"}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-mist-200">
                    {statement.free_cash_flow ? `$${Number(statement.free_cash_flow).toLocaleString()}` : "—"}
                  </td>
                  <td className="py-2 text-right font-mono text-mist-200">
                    {statement.eps_diluted ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {fundamentals && (
        <div className="panel p-6">
          <h2 className="text-lg font-semibold text-mist-100">Fundamentals & ratios</h2>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <Metric label="P/E" value={fmtRatio(fundamentals.pe_ratio)} />
            <Metric label="PEG" value={fmtRatio(fundamentals.peg_ratio)} />
            <Metric label="P/S" value={fmtRatio(fundamentals.price_to_sales)} />
            <Metric label="P/B" value={fmtRatio(fundamentals.price_to_book)} />
            <Metric label="EV/EBITDA" value={fmtRatio(fundamentals.ev_to_ebitda)} />
            <Metric label="Beta" value={fmtRatio(fundamentals.beta)} />
            <Metric label="Rev. growth" value={fmtRatio(fundamentals.revenue_growth_yoy, "%")} />
            <Metric label="EPS growth" value={fmtRatio(fundamentals.eps_growth_yoy, "%")} />
            <Metric label="Gross margin" value={fmtRatio(fundamentals.gross_margin, "%")} />
            <Metric label="Net margin" value={fmtRatio(fundamentals.net_margin, "%")} />
            <Metric label="ROE" value={fmtRatio(fundamentals.roe, "%")} />
            <Metric label="ROIC" value={fmtRatio(fundamentals.roic, "%")} />
            <Metric label="Debt/Equity" value={fmtRatio(fundamentals.debt_to_equity)} />
            <Metric label="Current ratio" value={fmtRatio(fundamentals.current_ratio)} />
            <Metric label="Div. yield" value={fmtRatio(fundamentals.dividend_yield, "%")} />
            <Metric label="Analyst rating" value={fmtRatio(fundamentals.analyst_rating_avg, "/5")} />
            <Metric label="52w high" value={fundamentals.week52_high ? `$${fmtRatio(fundamentals.week52_high)}` : "—"} />
            <Metric label="52w low" value={fundamentals.week52_low ? `$${fmtRatio(fundamentals.week52_low)}` : "—"} />
          </div>
        </div>
      )}

      {risk && (
        <div className="panel p-6">
          <h2 className="text-lg font-semibold text-mist-100">Standalone risk profile</h2>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Metric label="Volatility (ann.)" value={fmtRatio(risk.volatility_annualized_pct, "%", 1)} />
            <Metric label="Max drawdown" value={fmtRatio(risk.max_drawdown_pct, "%", 1)} />
            <Metric label="Sharpe ratio" value={fmtRatio(risk.sharpe_ratio)} />
            <Metric label="Sortino ratio" value={fmtRatio(risk.sortino_ratio)} />
          </div>
          <p className="mt-3 text-xs uppercase tracking-wide text-mist-400">
            Rating: <span className="text-mist-200">{risk.risk_rating}</span>
          </p>
        </div>
      )}

      {profile && (
        <div className="panel p-6">
          <h2 className="text-lg font-semibold text-mist-100">Company intelligence</h2>
          {profile.business_model && <p className="mt-2 text-sm text-mist-300">{profile.business_model}</p>}
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-mist-400">Economic moat</p>
              <p className="mt-1 text-sm capitalize text-mist-200">{profile.economic_moat}</p>
            </div>
            {profile.ceo_name && (
              <div>
                <p className="text-xs uppercase tracking-wide text-mist-400">CEO</p>
                <p className="mt-1 text-sm text-mist-200">
                  {profile.ceo_name}
                  {profile.ceo_since_year ? ` (since ${profile.ceo_since_year})` : ""}
                </p>
              </div>
            )}
          </div>
          {profile.competitive_advantages.length > 0 && (
            <div className="mt-4">
              <p className="text-xs uppercase tracking-wide text-mist-400">Competitive advantages</p>
              <ul className="mt-1 space-y-1 text-sm text-mist-200">
                {profile.competitive_advantages.map((item, i) => (
                  <li key={i}>• {item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div>
      <p className="text-xs text-mist-400">{label}</p>
      <p className="mt-1 font-mono text-sm text-mist-100">{value}</p>
    </div>
  );
}
